/**
 * Function signature type for event handlers.
 */
export type Fn = (arg: any) => void;

/**
 * EventEmitter Class
 *
 * The EventEmitter class provides a basic event handling mechanism. It allows objects to subscribe to and emit custom events.
 */
export class EventEmitter {
  protected _events: { [eventType: string]: Fn[] };

  /**
   * Constructor
   *
   * Initializes the EventEmitter instance.
   */
  constructor() {
    this._events = {};
  }

  /**
   * Subscribe to an Event
   *
   * @param {string} eventType - The name of the event to subscribe to.
   * @param {ViewerEvent} fn - The handler function to be called when the event occurs.
   */
  public on(eventType: string, fn: Fn): void {
    this._events[eventType] = this._events[eventType] || [];
    this._events[eventType].push(fn);
    return;
  }

  /**
   * Unsubscribe from an Event
   *
   * @param {string} eventType - The name of the event to unsubscribe from.
   * @param {ViewerEvent} fn - The handler function to remove.
   */
  public off(eventType: string, fn: Fn): void {
    if (!eventType) {
      this._events = {};
      return;
    }

    if (this._listens(eventType)) {
      const index = this._events[eventType].indexOf(fn);
      if (index >= 0) {
        this._events[eventType].splice(index, 1);
      }
      if (!this._events[eventType].length) {
        delete this._events[eventType];
      }
    }
    return;
  }

  /**
   * Fire an Event
   *
   * @param {string} eventType - The name of the event to trigger.
   * @param {unknown} data - Data to pass to event listeners.
   */
  public fire(eventType: string, data: unknown): void {
    if (!this._listens(eventType)) {
      return;
    }

    for (const fn of this._events[eventType]) {
      fn.call(this, data);
    }
    return;
  }

  /**
   * Check if an Event has Listeners
   *
   * @param {string} eventType - The name of the event to check.
   * @returns {boolean} - True if there are listeners for the event; otherwise, false.
   */
  private _listens(eventType: string): boolean {
    return eventType in this._events;
  }
}
